import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from "react-router-dom";
import { MathJax } from "better-react-mathjax";
import Solution3 from './solution/Solution3';
import Hints from './hints/Hints';
import SketchBoard from './sketch_board/SketchBoard';

import './ExercisePage.css';

const SOLVED_STORAGE_KEY = 'mbd.solvedExercises';

const readSolvedSet = () => {
    try {
        const raw = localStorage.getItem(SOLVED_STORAGE_KEY);
        return new Set(raw ? JSON.parse(raw) : []);
    } catch {
        return new Set();
    }
};

const writeSolvedSet = (set) => {
    try {
        localStorage.setItem(SOLVED_STORAGE_KEY, JSON.stringify(Array.from(set)));
    } catch {
        /* ignore quota errors */
    }
};

const prettify = (slug) =>
    String(slug || '')
        .replaceAll('_', ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());

const ExercisePage = () => {
    const params = useParams();
    const navigate = useNavigate();
    const { level, title, subTitle, section, id } = params;
    const exerciseId = Number(id);

    const [isSolutionVisible, setIsSolutionVisible] = useState(false);
    const [inputIndex, setInputIndex] = useState(0);
    const [areHintsVisible, setAreHintsVisible] = useState(false);
    const [solvedKey, setSolvedKey] = useState(`${title}/${subTitle}/${section}/${exerciseId}`);
    const [isSolved, setIsSolved] = useState(() => readSolvedSet().has(solvedKey));
    const boardRef = useRef(null);

    const jsonData = require(`../../data/${title}/${subTitle}.json`);

    const sectionExercises = jsonData[subTitle][section];
    const totalExercises = sectionExercises.length;
    const exerciseRecord = sectionExercises[exerciseId - 1];
    const ExerciseArray = exerciseRecord['exercise'];
    const HintsArray = exerciseRecord['hints'];

    const breadcrumb = useMemo(() => ({
        title: prettify(title),
        subTitle: prettify(subTitle),
        section: prettify(section),
    }), [title, subTitle, section]);

    const hasPrev = exerciseId > 1;
    const hasNext = exerciseId < totalExercises;

    const goToExercise = (newId) => {
        navigate(`/${level}/${title}/${subTitle}/${section}/${newId}`);
    };

    const showSolution = () => setIsSolutionVisible(true);
    const showHints = () => setAreHintsVisible(true);
    const hideHints = () => setAreHintsVisible(false);

    const closeSolution = (lastIndexFromSolution) => {
        setIsSolutionVisible(false);
        setInputIndex(lastIndexFromSolution);
    };

    const toggleSolved = () => {
        const next = readSolvedSet();
        if (next.has(solvedKey)) {
            next.delete(solvedKey);
            setIsSolved(false);
        } else {
            next.add(solvedKey);
            setIsSolved(true);
        }
        writeSolvedSet(next);
    };

    const goToSketchBoard = () => {
        boardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToTop();
    }, [exerciseId]);

    useEffect(() => {
        const key = `${title}/${subTitle}/${section}/${exerciseId}`;
        setSolvedKey(key);
        setIsSolved(readSolvedSet().has(key));
    }, [title, subTitle, section, exerciseId]);

    return (
        <div className='ExercisePage'>
            <nav className="exercise-breadcrumb" aria-label="Breadcrumb">
                <Link to="/titles">All Topics</Link>
                <span className="breadcrumb-sep">›</span>
                <Link to={`/advanced/${title}`}>{breadcrumb.title}</Link>
                <span className="breadcrumb-sep">›</span>
                <Link to={`/advanced/${title}/${subTitle}`}>{breadcrumb.subTitle}</Link>
                <span className="breadcrumb-sep">›</span>
                <span className="breadcrumb-current">
                    {breadcrumb.section} · Exercise {exerciseId} of {totalExercises}
                </span>
            </nav>

            <div id="exercise" className="exercise">
                <div className="exercise-header">
                    <div className="exercise-title">
                        <h5>Exercise {exerciseId}</h5>
                    </div>
                    {isSolved && (
                        <span className="exercise-solved-pill" aria-label="Solved">
                            ✓ Solved
                        </span>
                    )}
                </div>

                <div className="exercise-text">
                    <MathJax dynamic>
                        <div style={{ textAlign: "left" }} id="exercise-formulas">
                            <p style={{ whiteSpace: "pre-line" }}>{ExerciseArray}</p>
                        </div>
                    </MathJax>{" "}
                </div>

                <div>
                    {areHintsVisible && <Hints onClose={hideHints} hintsArray={HintsArray} />}
                </div>

                <div className="exercise-button-container">
                    <button
                        type="button"
                        className="exercise-action-btn exercise-action-btn--hint"
                        onClick={showHints}
                    >
                        Hints 💡
                    </button>
                    <button
                        type="button"
                        className="exercise-action-btn exercise-action-btn--primary"
                        onClick={goToSketchBoard}
                    >
                        Go to SketchBoard 🖊️
                    </button>
                    <button
                        type="button"
                        className="exercise-action-btn exercise-action-btn--secondary"
                        onClick={showSolution}
                    >
                        Show Solution 🧠
                    </button>
                    <button
                        type="button"
                        className={`exercise-action-btn exercise-action-btn--mark${isSolved ? ' is-solved' : ''}`}
                        onClick={toggleSolved}
                    >
                        {isSolved ? '✓ Solved' : 'Mark as Solved'}
                    </button>
                </div>
            </div>

            <div className="exercise-pagination" aria-label="Exercise navigation">
                <button
                    type="button"
                    className="exercise-pagination-btn"
                    onClick={() => goToExercise(exerciseId - 1)}
                    disabled={!hasPrev}
                >
                    ← Previous
                </button>
                <span className="exercise-pagination-position">
                    {exerciseId} / {totalExercises}
                </span>
                <button
                    type="button"
                    className="exercise-pagination-btn"
                    onClick={() => goToExercise(exerciseId + 1)}
                    disabled={!hasNext}
                >
                    Next →
                </button>
            </div>

            <div ref={boardRef} className="sketch-board-container">
                <SketchBoard />
            </div>
            {isSolutionVisible && (<Solution3 onClose={closeSolution} index={inputIndex}/>)}
        </div>
    );
};

export default ExercisePage;
